import React, { useState, useEffect } from "react";
import { addReview } from "../services/api";
import { FaStar, FaSpinner } from "react-icons/fa";
import "../styles/AddReviewForm.css";

const AddReviewForm = ({ gameId, onReviewAdded }) => {
  const [text, setText] = useState("");
  const [rating, setRating] = useState(5);
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [messageType, setMessageType] = useState(""); // "success" or "error"
  
  // Check if user is logged in
  const isLoggedIn = () => {
    const token = localStorage.getItem("token");
    if (!token) return false;
    
    try {
      // Check if token is expired (basic check)
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000 > Date.now();
    } catch {
      return false;
    }
  };

  // Check login status on mount and clear invalid tokens
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token && !isLoggedIn()) {
      localStorage.removeItem("token");
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!text.trim()) {
      setMessage("Please write a review before submitting.");
      setMessageType("error");
      return;
    }

    if (!isLoggedIn()) {
      setMessage("Please log in to submit a review. Your session may have expired.");
      setMessageType("error");
      return;
    }
    
    const token = localStorage.getItem("token");

    setIsSubmitting(true);
    setMessage("");
    
    try {
      const res = await addReview(gameId, text.trim(), rating, token);
      
      if (res.message) {
        setMessage(res.message);
        setMessageType("success");
        setText("");
        setRating(5);
        
        // Notify parent to reload reviews
        if (onReviewAdded) {
          onReviewAdded();
        }
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setMessage("");
          setMessageType("");
        }, 3000);
      } else {
        setMessage(res.error || "Review submitted successfully!");
        setMessageType("success");
      }
    } catch (err) {
      console.error("Review submission error:", err);
      
      // Handle specific error cases
      if (err.message.includes("Invalid token") || err.message.includes("401")) {
        setMessage("Your session has expired. Please log in again.");
        // Clear invalid token
        localStorage.removeItem("token");
      } else if (err.message.includes("403")) {
        setMessage("You don't have permission to submit reviews. Please log in.");
      } else {
        setMessage(err.message || "Failed to submit review. Please try again.");
      }
      
      setMessageType("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRatingChange = (newRating) => {
    setRating(newRating);
  };

  // If user is not logged in, show login prompt
  if (!isLoggedIn()) {
    return (
      <div className="add-review-form" data-component="add-review-form">
        <h3>Write a Review</h3>
        <div className="login-prompt">
          <p>You need to be logged in to write a review.</p>
          <div className="login-actions">
            <a href="/login" className="login-btn">Log In</a>
            <a href="/signup" className="signup-btn">Sign Up</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="add-review-form" data-component="add-review-form">
      <h3>Write a Review</h3>
      <form onSubmit={handleSubmit}>
        <div className="rating-section">
          <label>Rating:</label>
          <div className="star-rating">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                className={`star-btn ${star <= rating ? 'active' : ''}`}
                onClick={() => handleRatingChange(star)}
                disabled={isSubmitting}
              >
                <FaStar />
              </button>
            ))}
            <span className="rating-text">{rating} out of 5</span>
          </div>
        </div>

        <div className="text-section">
          <label htmlFor="review-text">Review:</label>
          <textarea
            id="review-text"
            required
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Share your thoughts about this game..."
            disabled={isSubmitting}
            rows={4}
          />
        </div>

        <button 
          type="submit" 
          className="submit-btn"
          disabled={isSubmitting || !text.trim()}
        >
          {isSubmitting ? (
            <>
              <FaSpinner className="spinner" />
              Submitting...
            </>
          ) : (
            'Submit Review'
          )}
        </button>

        {message && (
          <div className={`message ${messageType}`}>
            {message}
          </div>
        )}
      </form>
    </div>
  );
};

export default AddReviewForm;
